// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Normalises fontSize to a plain number (px).
 * Handles both `24` and `"24px"` / `"24"` values from the API.
 */
function parseFontSize(value) {
  if (typeof value === 'number') return value > 0 ? value : 0;
  if (typeof value === 'string') return parseFloat(value) || 0;
  return 0;
}

/**
 * Creates a canvas 2d context with the correct font string.
 * Returns null for degenerate input.
 */
function buildContext({ width, height, fontSize, fontFamily = 'sans-serif', fontWeight = 'normal' }) {
  if (!width || !height || !fontSize || width <= 0 || height <= 0 || fontSize <= 0) return null;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  // canvas font shorthand: "<weight> <size>px <family>"
  ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  return ctx;
}

/**
 * Returns the CSS line-height:normal equivalent for the current canvas font.
 *
 * Priority:
 *   1. fontBoundingBoxAscent + fontBoundingBoxDescent  (exact font metrics, W3C)
 *      — only trusted when within 0.8–1.5× fontSize; outside that range the
 *        web font hasn't loaded on the offscreen canvas and it fell back to a
 *        system font with very different metrics.
 *   2. actualBoundingBoxAscent + actualBoundingBoxDescent * 1.1  (glyph bounds + leading)
 *      — same sanity window applied.
 *   3. fontSize * 1.1  (safe fallback — matches browser `normal` for most fonts)
 *
 * NewTextLayer sets no explicit line-height, so the browser uses `normal`
 * which is typically ~1.0–1.2× font-size, NOT 1.4×.
 */
function getNormalLineHeight(ctx, fontSize) {
  const m = ctx.measureText('AÁgpqyj|');
  if (m.fontBoundingBoxAscent != null && m.fontBoundingBoxDescent != null) {
    const lh = m.fontBoundingBoxAscent + m.fontBoundingBoxDescent;
    // Sanity check: if the web font isn't loaded the canvas falls back to a
    // system font whose metrics can be 3–5× larger than the requested size.
    // Only trust the measurement when it looks plausible for the given fontSize.
    if (lh >= fontSize * 0.8 && lh <= fontSize * 1.5) return lh;
  }
  if (m.actualBoundingBoxAscent != null && m.actualBoundingBoxDescent != null) {
    const lh = (m.actualBoundingBoxAscent + m.actualBoundingBoxDescent) * 1.1;
    if (lh >= fontSize * 0.8 && lh <= fontSize * 1.5) return lh;
  }
  return fontSize * 1.1;
}

/**
 * Simulates the browser's word-wrap + word-break:break-word rendering.
 *
 * Mirrors NewTextLayer's CSS:
 *   overflowWrap: break-word   — long words break at any character
 *   wordBreak:    break-word   — same; allows arbitrary breaks
 *
 * Returns the number of visual lines the text would occupy.
 */
function countWrappedLines(text, ctx, maxWidth) {
  if (!text || maxWidth <= 0) return 0;

  let lineCount = 0;
  const paragraphs = text.split('\n');
  const spaceWidth = ctx.measureText(' ').width;

  for (const para of paragraphs) {
    if (para === '') {
      lineCount += 1;
      continue;
    }

    const words = para.split(' ');
    let lineWidth = 0;
    let firstOnLine = true;

    for (const word of words) {
      const wordWidth = ctx.measureText(word).width;
      const gap = firstOnLine ? 0 : spaceWidth;

      if (!firstOnLine && lineWidth + gap + wordWidth > maxWidth) {
        // Word doesn't fit on current line — wrap
        lineCount += 1;
        lineWidth = 0;
        firstOnLine = true;
      }

      if (wordWidth > maxWidth) {
        // Word wider than the entire box — break character-by-character
        if (!firstOnLine) {
          lineCount += 1;
          lineWidth = 0;
        }
        for (const char of word) {
          const cw = ctx.measureText(char).width;
          if (lineWidth + cw > maxWidth && lineWidth > 0) {
            lineCount += 1;
            lineWidth = 0;
          }
          lineWidth += cw;
        }
        firstOnLine = false;
      } else {
        lineWidth += (firstOnLine ? 0 : gap) + wordWidth;
        firstOnLine = false;
      }
    }

    lineCount += 1; // the final (possibly partial) line of this paragraph
  }

  return lineCount;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns the theoretical maximum number of characters that fit inside the
 * text box described by `model`, using real canvas measureText.
 *
 * Uses average character width over a mixed sample (not 'a' × N) to match
 * the width distribution of real prose text.  Line count is derived from
 * accurate CSS-normal line-height font metrics.
 *
 * Memoize this with `useMemo` keyed on the model's dimension/font fields —
 * it only needs to run when the box or font changes, not on every keystroke.
 *
 * @param {{
 *   width:       number,
 *   height:      number,
 *   fontSize:    number | string,   // accepts "24px" or 24
 *   fontFamily?: string,
 *   fontWeight?: string | number,
 * }} model
 * @returns {number}  0 when the box cannot hold any text
 */
export function calculateMaxCharacters(model) {
  const fontSize = parseFontSize(model?.fontSize);
  const ctx = buildContext({ ...model, fontSize });
  if (!ctx) return 0;

  const lineHeight = getNormalLineHeight(ctx, fontSize);
  const maxLines = Math.floor((model.height) / lineHeight);
  if (maxLines <= 0) return 0;

  // Mixed-case + punctuation sample gives a realistic average char width.
  const sample =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789 .,!?\'-';
  const avgCharWidth = ctx.measureText(sample).width / sample.length;
  if (avgCharWidth <= 0) return 0;

  const charsPerLine = Math.floor(model.width / avgCharWidth);
  if (charsPerLine <= 0) return 0;

  return charsPerLine * maxLines;
}

/**
 * Returns true when `text` fits inside the box WITHOUT overflowing.
 *
 * This operates on the user's actual typed characters (after stripping HTML
 * tags from MarkdownEditor), so it is precise and should be called from
 * Formik's `validate` on every value change.
 *
 * @param {string} text
 * @param {{
 *   width:       number,
 *   height:      number,
 *   fontSize:    number | string,
 *   fontFamily?: string,
 *   fontWeight?: string | number,
 * }} model
 * @returns {boolean}
 */
export function doesTextFit(text, model) {
  if (!text) return true;

  const fontSize = parseFontSize(model?.fontSize);
  const ctx = buildContext({ ...model, fontSize });
  if (!ctx) return true;

  const lineHeight = getNormalLineHeight(ctx, fontSize);
  const maxLines = Math.floor(model.height / lineHeight);
  if (maxLines <= 0) return false;

  // Convert block-level closing tags to newlines before stripping all tags,
  // so paragraph and line-break boundaries are preserved for wrap counting.
  const plain = String(text)
    .replace(/<\/p\s*>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .trim();
  const lines = countWrappedLines(plain, ctx, model.width);

  return lines <= maxLines;
}
